import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ArrowLeft, ExternalLink } from 'lucide-react'
import { ConsentFlags, EmailConfidence, OutreachBadge, StatusBadge } from '../components/ProspectBadges'
import { SuppressDialog } from '../components/SuppressDialog'
import { useProspect, useProspectEvents } from '../hooks/useProspects'

export function ProspectDetailPage() {
  const { prospectId } = useParams<{ prospectId: string }>()
  const navigate = useNavigate()
  const [suppressOpen, setSuppressOpen] = useState(false)

  const { data, isLoading } = useProspect(prospectId ?? null)
  const { data: eventsData } = useProspectEvents(prospectId ?? null)

  if (isLoading || !data) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const { prospect, content, sections } = data
  const captions = content.filter((c) => c.kind === 'caption')

  return (
    <div className="space-y-6 p-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/app/prospects')}>
        <ArrowLeft className="mr-1 h-4 w-4" /> Prospects
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{prospect.displayName || prospect.handle}</h1>
          <p className="text-sm text-slate-500">@{prospect.handle}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusBadge status={prospect.status} />
            <OutreachBadge status={prospect.outreachStatus} />
            <ConsentFlags prospect={prospect} />
          </div>
        </div>
        <Button variant="destructive" onClick={() => setSuppressOpen(true)}>
          Suppress
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Contact</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <EmailConfidence prospect={prospect} />
            {prospect.websiteUrl && (
              <a
                href={prospect.websiteUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
              >
                {prospect.websiteUrl} <ExternalLink className="h-3 w-3" />
              </a>
            )}
            <p className="text-xs text-slate-500">
              {prospect.contactAttempts} attempt{prospect.contactAttempts === 1 ? '' : 's'}
              {prospect.repliedAt ? ' · replied' : ''}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Material</CardTitle></CardHeader>
          <CardContent className="text-sm">
            <p>{captions.length} captions</p>
            <p>{sections.length} biography chapters</p>
            <p className="text-xs text-slate-500">
              readiness {prospect.readinessTier ?? '—'}
              {prospect.readinessScore != null ? ` (${prospect.readinessScore})` : ''}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Reach</CardTitle></CardHeader>
          <CardContent className="text-sm">
            <p className="tabular-nums">{prospect.followers?.toLocaleString() ?? '—'} followers</p>
            <p className="text-xs text-slate-500">{prospect.categoryName ?? ''}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="bio">
        <TabsList>
          <TabsTrigger value="bio">Biography ({sections.length})</TabsTrigger>
          <TabsTrigger value="captions">Their words ({captions.length})</TabsTrigger>
          <TabsTrigger value="events">Activity ({eventsData?.events.length ?? 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="bio" className="space-y-3 pt-4">
          {!sections.length && <p className="text-sm text-slate-500">No chapters drafted.</p>}
          {sections.map((s) => (
            <Card key={s.id}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{s.title}</CardTitle>
                {s.description && <CardDescription>{s.description}</CardDescription>}
              </CardHeader>
              <CardContent>
                {/* Model-authored HTML, already run through the shared sanitizer on write
                    AND on serve (lib/content/easel/bio-sanitize.js — allow-list, no
                    images, no links, no script). Rendering it is safe because of that
                    invariant, not because it looks safe here. */}
                <div
                  className="prose prose-sm max-w-none text-slate-700"
                  dangerouslySetInnerHTML={{ __html: s.contentHtml }}
                />
                <p className="pt-2 text-xs text-slate-400">
                  {s.contentRefs?.length ?? 0} posts cited · {s.status}
                </p>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="captions" className="space-y-2 pt-4">
          {captions.map((c) => (
            <Card key={c.id}>
              <CardContent className="pt-4 text-sm text-slate-700">{c.body}</CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="events" className="pt-4">
          <div className="space-y-2">
            {(eventsData?.events ?? []).map((e) => (
              <div key={e.id} className="flex gap-3 border-b border-slate-100 py-2 text-sm">
                <span className="w-40 shrink-0 text-xs text-slate-500">
                  {new Date(e.createdAt).toLocaleString()}
                </span>
                <span className="w-40 shrink-0 font-medium">{e.kind}</span>
                <span className="text-xs text-slate-500">{e.actor}</span>
              </div>
            ))}
            {!eventsData?.events.length && (
              <p className="text-sm text-slate-500">Nothing recorded yet.</p>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <SuppressDialog prospect={prospect} open={suppressOpen} onOpenChange={setSuppressOpen} />
    </div>
  )
}
